<template>
  <div class="questionnaire-prompt-container">
    <k-panel :hasContentToggle="false">
      <template #body>
        <section class="header-container">
          <h2 class="prompt-container">
            <span class="prompt-number">{{ prompt.number }}.</span>
            <span class="prompt" v-html="getMarkdown(prompt.prompt)"></span>
          </h2>
          <button v-if="supportsAiFeedback"
            :disabled="disableFeedbackButton"
            type="button"
            class="modal-default-button btn btn-outlined feedback-modal-button"
            @click="openFeedbackModal">
              <img :src="chatbotImage" class="chatbot-icon-small" alt="KATE bot avatar"/>
            Ask KATE for Feedback
          </button>
        </section>
        <section class="response">
          <k-text-editor
            v-if="responseType === 'LONG_TEXT' || responseType === 'SHORT_TEXT'"
            v-model="currentResponse"
            :maxLength="maxTextLength"
            :placeholder="textPlaceholder"
            :read-only="readOnly"
            :disable-toolbar="disableTextToolbar"
            :posthog-no-capture="true"
          ></k-text-editor>
          <k-percentage-slider
            v-if="responseType === 'QUANTITATIVE_PERCENT'"
            :read-only="readOnly"
            v-model="currentResponse"
            :posthog-no-capture="true"
          ></k-percentage-slider>
          <k-number-choice
            v-if="responseType === 'QUANTITATIVE_VALUE'"
            :read-only="readOnly"
            v-model="currentResponse"
            :posthog-no-capture="true"
          ></k-number-choice>
          <k-multi-choice
            v-if="responseType === 'MULTIPLE_CHOICE'"
            v-model="currentResponse"
            :readOnly="readOnly"
            :choices="prompt.answers.choices"
            :choiceAmount="prompt.answers.choice_amount"
            :posthog-no-capture="true"
          ></k-multi-choice>
          <k-numeric-input
              v-if="responseType === 'BKSB_SCORES'"
              v-model="currentResponse"
              :min="0"
              :max="3"
              :step="0.1"
              :precision="1"
              :readOnly="readOnly"
              inputPlaceholder="Enter a number"
              :posthog-no-capture="true"
          ></k-numeric-input>
          <k-numeric-input
              v-if="responseType === 'OTJ_HOURS'"
              v-model="currentResponse"
              :min="0"
              :step="1"
              :precision="0"
              :readOnly="readOnly"
              inputPlaceholder="Enter a number"
              :posthog-no-capture="true"
          ></k-numeric-input>
          <k-date-picker
              v-if="responseType === 'DATE'"
              v-model="currentResponse"
              :readOnly="readOnly"
              inputPlaceholder="Select a date"
              :posthog-no-capture="true"
          ></k-date-picker>
          <k-multi-choice
            v-if="responseType === 'YES_NO'"
            v-model="currentResponse"
            :readOnly="readOnly"
            :choiceAmount="1"
            :choices="['Yes', 'No']"
            :posthog-no-capture="true"
          ></k-multi-choice>
        </section>
      </template>
    </k-panel>

    <questionnaire-feedback-modal v-if="supportsAiFeedback"
      :showModal="showFeedbackModal"
      :prompt="prompt"
      :currentResponse="currentResponse"
      @close="closeFeedbackModal"
    ></questionnaire-feedback-modal>
  </div>
</template>

<style>
.prompt-container span p {
  color: var(--kate-type-light);
}

.prompt-container .prompt,
.prompt-container .prompt p,
.prompt-container .prompt em,
.prompt-container .prompt i {
  font-family: hk-grotesk, sans-serif;
  font-weight: normal;
}

.prompt-container .prompt em,
.prompt-container .prompt i {
  font-style: italic;
}

.prompt-container .prompt strong {
  font-family: hk-grotesk-bold, sans-serif;
}

.prompt-container .prompt b {
  font-family: hk-grotesk-semibold, sans-serif;
}
</style>

<style scoped>
.prompt {
  width: 95%;
}

.header-container > button {
  margin-left: auto;
  float: right;
  margin-bottom: 10px;
}

.prompt-container {
  display: flex;
  flex-wrap: nowrap;
  font-size: 1.35em;
}

.prompt-number {
  padding-right: 5px;
  min-width: 30px;
}

.prompt-container span p {
  color: var(--kate-type-light);
}

.feedback-modal-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.chatbot-icon-small {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}
</style>

<script>
import KTextEditor from '@base-components/k-text-editor.vue';
import KPanel from '@base-components/k-panel.vue';
import KPercentageSlider from '@base-components/k-percentage-slider.vue';
import KNumberChoice from '@base-components/k-number-choice.vue';
import KMultiChoice from '@base-components/k-multi-choice.vue';
import { markdown } from '@utils/maths-markdown';
import { importImage } from '@utils/dynamic-image-importer';
import KNumericInput from '@base-components/k-numeric-input.vue';
import KDatePicker from '@base-components/k-date-picker.vue';
import QuestionnaireFeedbackModal from '../feedback/components/questionnaire-feedback-modal.vue';

export default {
  components: {
    KNumericInput,
    KPanel,
    KTextEditor,
    KPercentageSlider,
    KNumberChoice,
    KMultiChoice,
    KDatePicker,
    QuestionnaireFeedbackModal,
  },

  props: {
    prompt: {
      type: Object,
    },
    previousResponse: {
      type: [Array, String, Number],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disableTextToolbar: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentResponse: undefined,
      showFeedbackModal: false,
      emitUpdates: false,
    };
  },

  mounted() {
    this.currentResponse = this.previousResponse;
    this.$nextTick(() => {
      this.emitUpdates = true;
    });
  },

  watch: {
    currentResponse() {
      if (!this.emitUpdates) {
        return;
      }
      this.$emit('update', this.currentResponse);
    },
    previousResponse(val) {
      this.emitUpdates = false;
      this.currentResponse = val;
      this.$nextTick(() => {
        this.emitUpdates = true;
      });
    },
  },

  computed: {
    chatbotImage() {
      return importImage('icons/kate-bot-chat-colours.svg');
    },
    maxValue() {
      if (this.prompt.response_type === 'QUANTITATIVE_PERCENT') {
        return 100;
      }
      if (this.prompt.response_type === 'QUANTITATIVE_VALUE') {
        return 10;
      }
      return undefined;
    },
    maxTextLength() {
      return this.prompt.response_type === 'SHORT_TEXT' ? 512 : 10000;
    },
    textPlaceholder() {
      if (this.prompt.response_type === 'LONG_TEXT') {
        return 'Enter your response here...';
      }
      if (this.prompt.response_type === 'SHORT_TEXT') {
        return `Enter your response here - only a short response is required (max ${this.maxTextLength} characters)`;
      }
      return undefined;
    },
    responseType() {
      return this.prompt.response_type || this.prompt.responseType;
    },
    supportsAiFeedback() {
      return ((this.responseType === 'LONG_TEXT' || this.responseType === 'SHORT_TEXT') && this.prompt.ai_feedback_enabled);
    },
    disableFeedbackButton() {
      return !(this.currentResponse && this.currentResponse?.trim().length > 0);
    },
  },

  methods: {
    openFeedbackModal() {
      this.showFeedbackModal = true;
    },
    closeFeedbackModal() {
      this.showFeedbackModal = false;
    },
    clearResponse() {
      this.currentResponse = undefined;
    },
    getMarkdown(str) {
      return markdown(str);
    },
  },
};
</script>
